import React from 'react'
import { connect } from 'react-redux'
import { Button, Popconfirm, Switch, Table, Tag, message } from 'antd'
import Permission from '@/components/PermissionA5'
import { parseHmStringToDayMinutes, renderTextStr } from '@/utils/renderUtils'
import { fetchTuinaShiftList } from '@/redux/tuina/shiftListSlice'
import { fetchTuinaShiftOptions } from '@/redux/tuina/shiftOptionsSlice'
import { deleteTuinaShift, updateTuinaShift } from '@/service/tuina'
import ShiftModal from './ShiftModal'
import './ShiftList.less'

/** 列表默认查询（仅组件侧使用，不写入 Redux initialState） */
const DEFAULT_TUINA_SHIFT_LIST_QUERY = {
  pageNumber: 1,
  pageSize: 20
}

const mergeTuinaShiftListQuery = (reduxQuery) =>
  reduxQuery && Object.keys(reduxQuery).length > 0 ? reduxQuery : DEFAULT_TUINA_SHIFT_LIST_QUERY

function formatShiftClock (raw) {
  const minutes = parseHmStringToDayMinutes(raw)
  if (minutes == null) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function formatShiftRange (start, end) {
  const startM = parseHmStringToDayMinutes(start)
  const endM = parseHmStringToDayMinutes(end)
  if (startM == null || endM == null || startM >= endM) return renderTextStr('')
  return `${formatShiftClock(start)}～${formatShiftClock(end)}`
}

function shiftEditBody (record, isDefault) {
  const body = {
    storeId: record.storeId,
    name: record.name,
    startTime: record.startTime,
    endTime: record.endTime,
    bookable: record.bookable,
    sortOrder: record.sortOrder,
    isDefault: isDefault === true
  }
  if (record.restStartTime != null && record.restStartTime !== '') {
    body.restStartTime = record.restStartTime
  }
  if (record.restEndTime != null && record.restEndTime !== '') {
    body.restEndTime = record.restEndTime
  }
  return body
}

class ShiftList extends React.Component {
  state = {
    defaultSubmittingId: null
  }

  componentDidMount () {
    const { storeId } = this.props
    const query = { ...DEFAULT_TUINA_SHIFT_LIST_QUERY }
    if (storeId != null && storeId !== '') query.storeId = storeId
    this.fetchList(query)
  }

  fetchList = (formatQuery) => {
    this.props
      .fetchTuinaShiftList({ query: formatQuery })
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载班次失败'))
  }

  handleTableChange = (pagination) => {
    const query = mergeTuinaShiftListQuery(this.props.shiftList.query)
    this.fetchList({
      ...query,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    })
  }

  handleModalSuccess = () => {
    this.fetchList(mergeTuinaShiftListQuery(this.props.shiftList.query))
    const { storeId } = this.props
    const query = {}
    if (storeId != null && storeId !== '') query.storeId = storeId
    this.props.fetchTuinaShiftOptions({ query })
  }

  handleIsDefaultChange = async (record, checked) => {
    if (record.isDefault === true && checked) return
    if (record.isDefault !== true && !checked) return
    const list = Array.isArray(this.props.shiftList.data.list) ? this.props.shiftList.data.list : []
    this.setState({ defaultSubmittingId: record.id })
    try {
      if (checked) {
        const others = list.filter((row) => (
          row.isDefault === true && String(row.id) !== String(record.id)
        ))
        for (let i = 0; i < others.length; i += 1) {
          const row = others[i]
          await updateTuinaShift(row.id, shiftEditBody(row, false))
        }
      }
      await updateTuinaShift(record.id, shiftEditBody(record, checked))
      message.success(checked ? '已设为默认班次' : '已取消默认班次')
      this.handleModalSuccess()
    } catch (e) {
      this.handleModalSuccess()
    } finally {
      this.setState({ defaultSubmittingId: null })
    }
  }

  handleDelete = async (record) => {
    try {
      await deleteTuinaShift(record.id)
    } catch (e) {
      return
    }
    message.success('已删除')
    this.handleModalSuccess()
  }

  storeName = (storeId) => {
    const options = this.props.getJStoreList.data
    if (!Array.isArray(options)) return storeId
    const row = options.find((item) => String(item.id) === String(storeId))
    return row ? row.value : storeId
  }

  getColumns = () => [
    {
      title: '名称',
      dataIndex: 'name',
      render: (v) => renderTextStr(v)
    },
    {
      title: '上班',
      key: 'work',
      width: 148,
      render: (_, record) => (
        <span className="tuina-shift-meta">{formatShiftRange(record.startTime, record.endTime)}</span>
      )
    },
    {
      title: '休息',
      key: 'rest',
      width: 148,
      render: (_, record) => (
        <span className="tuina-shift-meta">{formatShiftRange(record.restStartTime, record.restEndTime)}</span>
      )
    },
    {
      title: '默认',
      dataIndex: 'isDefault',
      width: 88,
      render: (v, record) => (
        <Permission rootPath="tuinaPage">
          <span pathitem={['roster']} checkarray={['buttonCodes']}>
            <Popconfirm
              title="修改会师傅可约时间"
              description="是否执行？"
              okText="执行"
              cancelText="取消"
              overlayClassName="tuina-shift-popconfirm"
              disabled={this.state.defaultSubmittingId != null}
              onConfirm={() => this.handleIsDefaultChange(record, v !== true)}
            >
              <span>
                <Switch
                  checked={v === true}
                  loading={String(this.state.defaultSubmittingId) === String(record.id)}
                  disabled={this.state.defaultSubmittingId != null}
                />
              </span>
            </Popconfirm>
          </span>
        </Permission>
      )
    },
    {
      title: '可约',
      dataIndex: 'bookable',
      width: 80,
      render: (v) => {
        if (v !== true && v !== false) return renderTextStr('')
        const text = v ? '可约' : '不可约'
        return <Tag color={v ? 'success' : 'default'}>{text}</Tag>
      }
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      width: 64,
      render: (v) => {
        const n = Number(v)
        if (v == null || v === '' || !Number.isFinite(n)) return renderTextStr('')
        return <span className="tuina-shift-meta">{n}</span>
      }
    },
    {
      title: '操作',
      key: 'actions',
      width: 168,
      render: (_, record) => (
        <Permission rootPath="tuinaPage">
          <div pathitem={['roster']} checkarray={['buttonCodes']} className="tuina-shift-actions">
            <ShiftModal actType="edit" record={record} onSuccess={this.handleModalSuccess}>
              <Button type="link" aria-label={`改 ${record.name}`}>编辑</Button>
            </ShiftModal>
            <Popconfirm
              title="确认删除该班次？"
              description={record.name}
              okText="删除"
              cancelText="取消"
              overlayClassName="tuina-shift-popconfirm"
              okButtonProps={{ danger: true }}
              onConfirm={() => this.handleDelete(record)}
            >
              <Button type="link" danger aria-label={`删 ${record.name}`}>删除</Button>
            </Popconfirm>
          </div>
        </Permission>
      )
    }
  ]

  render () {
    const { shiftList } = this.props
    const { fetching, query: reduxQuery, data } = shiftList
    const query = mergeTuinaShiftListQuery(reduxQuery)
    const list = Array.isArray(data.list) ? data.list : []
    const totalRow = data.totalRow || 0

    return (
      <div>
        <section className="tuina-shift-toolbar">
          <div className="toolbar-btns">
            <Permission rootPath="tuinaPage">
              <span pathitem={['roster']} checkarray={['buttonCodes']}>
                <ShiftModal actType="create" onSuccess={this.handleModalSuccess}>
                  <Button type="primary" size="large">新增</Button>
                </ShiftModal>
              </span>
            </Permission>
          </div>
        </section>
        <Table
        className="tuina-shift-table"
        rowKey={(r) => String(r.id)}
        columns={this.getColumns()}
        dataSource={list}
        loading={fetching}
        onChange={this.handleTableChange}
        scroll={{ x: 896 }}
        pagination={{
          current: query.pageNumber,
          pageSize: query.pageSize,
          total: totalRow,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          pageSizeOptions: ['10', '20', '50']
        }}
      />
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  shiftList: state.tuina.shiftList,
  getJStoreList: state.components.getJStoreList
})

const mapDispatchToProps = {
  fetchTuinaShiftList,
  fetchTuinaShiftOptions
}

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(ShiftList)
